// Restoring Eng Tour stop content from an export zip — the pure half.
//
// An editor reassigned a stop onto a room that already had a stop (E2-262,
// "Student Services") and saved; the content of that room's stop was written
// over in every tour and in the stop bank at once, because a stop's content is
// shared by room code (`saveStopForAllTours`). Edit History holds the previous
// save, but so does the operator's own ⬇ Export zip, and that is what this
// module reads: which documents the zip holds, which stops each one has, and
// how a chosen stop's CONTENT goes back into the current data without moving
// anything.
//
// Three decisions live here and are pinned by src/engtour.restore.test.js:
//
//  1. WHAT IS CONTENT. `CONTENT_FIELDS` is what a restore puts back — the
//     words, pictures, quote and badges a visitor sees. Everything that says
//     WHERE the stop is today (building, room, label, nodeId, lat/lng, floor,
//     the fallback room) is left alone: the backup describes a room's content,
//     not where the room was pinned a month ago. `guidance` (per-tour What's
//     Next) and the bank's `freeRoam` star belong to the tour and the bank
//     respectively, not to the stop, and stay too.
//  2. IDENTITY IS `stopKey` (src/utils/engtourRoutes.js), the bank's own key:
//     the pinned node, else building + room. The stop that was overwritten is
//     found in the current data by that key, in every tour and in the bank.
//  3. A RESTORE PUTS BACK EXACTLY WHAT THE BACKUP HAD, including a field the
//     mistake filled and the backup left blank — so an absent field on the pick
//     is written as its empty value, never skipped.
//
// Reading the zip's bytes is `readZip` in ./zip.js; fetching, choosing and
// saving live in EngTourCreator. This file imports nothing but `stopKey`.

import { stopKey } from './engtourRoutes.js';

/** The fields a restore puts back. Everything else about a stop is where/how it sits today. */
export const CONTENT_FIELDS = Object.freeze([
  'title', 'subtitle', 'text', 'images', 'videoUrl', 'quote', 'departments', 'icon', 'primary', 'type',
]);

/** The fields that say where a stop is; copied onto a bank entry that has to be created, never onto one that exists. */
export const LOCATION_FIELDS = Object.freeze([
  'building', 'room', 'label', 'nodeId', 'lat', 'lng', 'floor', 'fallbackBuilding', 'fallbackRoom',
]);

// Mirrors SHORT_CODES in ./engtourRoomNames.js (which this file does not import
// so it stays a leaf): the creator's building key → the code it prints.
const SHORT_CODES = { e1: 'E1', e2: 'E2', e3: 'E3', stanley_pauley_centre: 'SP-C', stanley_pauley_engineering: 'SP' };
const normBuilding = (b) => (b == null ? '' : String(b).toLowerCase().replace(/^eitc[\s_-]*/, '').trim());

/** "E2-262" for a room stop; a hand-placed spot is named by its label. */
export function stopCode(s) {
  if (!s) return '';
  if (s.nodeId) {
    const label = String(s.label || s.name || '').trim();
    if (label) return label;
  }
  const b = normBuilding(s.building);
  const code = SHORT_CODES[b] || b.toUpperCase();
  const room = s.room == null ? '' : String(s.room).trim();
  return room ? `${code}-${room}` : code;
}

const emptyFor = (field) => (field === 'images' || field === 'departments' ? [] : field === 'primary' ? false : '');

const isEmpty = (v) => (
  v == null
  || (typeof v === 'string' && v.trim() === '')
  || (Array.isArray(v) && v.length === 0)
  || v === false
);

const clone = (v) => (Array.isArray(v) ? v.map(clone) : v && typeof v === 'object' ? { ...v } : v);

/** A stop's content, every CONTENT_FIELD present, absent ones as their empty value. */
export function contentOf(stop) {
  const out = {};
  for (const f of CONTENT_FIELDS) {
    const v = stop ? stop[f] : undefined;
    out[f] = v == null ? emptyFor(f) : clone(v);
  }
  return out;
}

/** Does any CONTENT_FIELD carry something? */
// "Written" means somebody typed or attached something: a stop carrying only
// its `type` ("room"), an icon or a primary flag is still an empty shell.
const WRITTEN_FIELDS = ['title', 'subtitle', 'text', 'images', 'videoUrl', 'quote', 'departments'];
export function hasContent(stop) {
  return WRITTEN_FIELDS.some((f) => !isEmpty(stop?.[f]));
}

const same = (a, b) => {
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((x, i) => same(x, b[i]));
  }
  if (a && b && typeof a === 'object' && typeof b === 'object') return JSON.stringify(a) === JSON.stringify(b);
  return a === b;
};

/** The CONTENT_FIELDS on which two stops differ (absent and empty read the same). */
export function diffContent(a, b) {
  const ca = contentOf(a), cb = contentOf(b);
  return CONTENT_FIELDS.filter((f) => !same(ca[f], cb[f]));
}

// ── Which documents a backup holds ───────────────────────────────────────────

const isToursDoc = (d) => !!d && typeof d === 'object' && !Array.isArray(d) && Array.isArray(d.tours);

const parseJson = (text) => {
  try { return JSON.parse(text); } catch { return undefined; }
};

/**
 * The entry's text, from `text` or by decoding `data`; only .json files are
 * ever decoded. `ArrayBuffer.isView` rather than `instanceof Uint8Array`: a
 * typed array from another realm (a Worker, Jest's jsdom) fails instanceof.
 */
function textOf(entry) {
  if (typeof entry?.text === 'string') return entry.text;
  if (entry?.data && (ArrayBuffer.isView(entry.data) || entry.data instanceof ArrayBuffer)) {
    return new TextDecoder('utf-8').decode(entry.data);
  }
  return null;
}

/** Does `path`, under any root folder, end in `tail` ("live/tours.json")? */
const endsWithPath = (path, tail) => path === tail || String(path).endsWith(`/${tail}`);

const localDate = (ts) => {
  const d = new Date(ts);
  return Number.isFinite(d.getTime()) ? d.toLocaleString() : String(ts);
};

const historyLabel = (row) => {
  const when = row.savedAt || localDate(row.ts);
  const who = row.user || 'shared password';
  const change = Array.isArray(row.changes) && row.changes.length ? String(row.changes[0]) : '';
  return [when, who, change].filter(Boolean).join(' · ');
};

/**
 * The documents a backup offers, newest first: `[{ id, label, ts, data }]`.
 * `entries` is the zip as `[{ path, text | data }]` (any root folder, images
 * and README ignored, a malformed JSON entry skipped) or a single parsed tours
 * document. A zip that holds no export layout but some `.json` file that IS a
 * tours document still offers that file, so a re-packed `tours.json` works.
 */
export function backupSources(entries) {
  if (isToursDoc(entries)) return [{ id: 'file', label: 'This file', ts: null, data: entries }];
  if (!Array.isArray(entries)) return [];

  const jsonEntries = entries.filter((e) => e && typeof e.path === 'string' && /\.json$/i.test(e.path));
  const read = (e) => { const t = textOf(e); return t == null ? undefined : parseJson(t); };
  const find = (tail) => jsonEntries.find((e) => endsWithPath(e.path, tail));

  const out = [];
  const liveEntry = find('live/tours.json');
  const live = liveEntry ? read(liveEntry) : undefined;
  if (isToursDoc(live)) out.push({ id: 'live', label: 'Live data when the export was taken', ts: null, data: live });

  const indexEntry = find('history/index.json');
  const index = indexEntry ? read(indexEntry) : undefined;
  const history = [];
  if (Array.isArray(index)) {
    for (const row of index) {
      if (!row || typeof row !== 'object' || !row.file) continue;
      const entry = jsonEntries.find((e) => endsWithPath(e.path, String(row.file)));
      if (!entry) continue;
      const data = read(entry);
      if (!isToursDoc(data)) continue;
      const ts = Number(row.ts);
      history.push({ id: `history:${row.ts}`, label: historyLabel(row), ts: Number.isFinite(ts) ? ts : null, data });
    }
  }
  history.sort((a, b) => (b.ts ?? -Infinity) - (a.ts ?? -Infinity));
  out.push(...history);

  if (!out.length) {
    for (const e of jsonEntries) {
      const data = read(e);
      if (isToursDoc(data)) out.push({ id: `file:${e.path}`, label: e.path, ts: null, data });
    }
  }
  return out;
}

// ── Which stops a document holds ─────────────────────────────────────────────

const tourName = (t, i) => `Tour ${t?.id ?? t?.title ?? i + 1}`;

const naturalCompare = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });

/**
 * One row per distinct stop in a tours document, across every tour and the
 * stop bank: `[{ key, code, title, stop, hasContent, where }]`, sorted by code.
 * `stop` is the first TOUR stop seen with that key (the bank entry when no tour
 * has it) — the object a restore is made from. `where` names each place it
 * appears ("Tour B #3", "bank").
 */
export function restorableStops(data) {
  const rows = new Map();
  const add = (stop, where) => {
    const key = stopKey(stop);
    if (!key || key === '::') return; // nothing to identify it by
    const row = rows.get(key);
    if (row) { row.where.push(where); return; }
    rows.set(key, { key, code: stopCode(stop), title: String(stop.title || ''), stop, hasContent: hasContent(stop), where: [where] });
  };
  (data?.tours || []).forEach((t, ti) => (t?.stops || []).forEach((s, si) => { if (s) add(s, `${tourName(t, ti)} #${si + 1}`); }));
  (data?.stopBank || []).forEach((s) => { if (s) add(s, 'bank'); });
  return Array.from(rows.values()).sort((a, b) => naturalCompare(a.code, b.code) || naturalCompare(a.key, b.key));
}

// ── Putting content back ─────────────────────────────────────────────────────

/**
 * `picks` is `[{ key, stop }]` — rows from `restorableStops` of the backup.
 * Returns `{ data, report }` where `data` is a copy of `current` with every
 * tour stop and bank entry keyed `key` carrying the pick's CONTENT_FIELDS
 * (absent ones written as their empty value), and a pick whose key appears
 * nowhere appended to the bank as a new, un-starred entry. Nothing else
 * changes and `current` is never mutated.
 */
export function restoreStops(current, picks) {
  const list = (Array.isArray(picks) ? picks : []).filter((p) => p && p.stop);
  const report = [];
  if (!list.length) return { data: current, report };

  // `targetKey` aims a backup stop at a DIFFERENT current stop: a stop whose
  // room was a name in the backup ("e2::umes lounge") and is a number now
  // ("e2::292") can only be matched by somebody saying so.
  const byKey = new Map();
  for (const p of list) {
    const from = p.key || stopKey(p.stop);
    const key = typeof p.targetKey === 'string' && p.targetKey ? p.targetKey : from;
    if (key && key !== '::') byKey.set(key, { key, from, content: contentOf(p.stop), stop: p.stop, tourStops: 0, bankEntries: 0 });
  }

  const tours = (current?.tours || []).map((t) => {
    if (!t || !Array.isArray(t.stops)) return t;
    let changed = false;
    const stops = t.stops.map((s) => {
      const hit = s && byKey.get(stopKey(s));
      if (!hit) return s;
      hit.tourStops++;
      changed = true;
      return { ...s, ...contentOf(hit.stop) };
    });
    return changed ? { ...t, stops } : t;
  });

  let bank = Array.isArray(current?.stopBank) ? current.stopBank.map((s) => {
    const hit = s && byKey.get(stopKey(s));
    if (!hit) return s;
    hit.bankEntries++;
    return { ...s, ...contentOf(hit.stop) }; // freeRoam rides along untouched
  }) : current?.stopBank;

  for (const hit of byKey.values()) {
    const added = hit.tourStops === 0 && hit.bankEntries === 0;
    if (added) {
      const entry = {};
      for (const f of LOCATION_FIELDS) if (hit.stop[f] !== undefined) entry[f] = clone(hit.stop[f]);
      Object.assign(entry, hit.content, { freeRoam: false });
      bank = [...(Array.isArray(bank) ? bank : []), entry];
    }
    report.push({ key: hit.key, from: hit.from, code: stopCode(hit.stop), tourStops: hit.tourStops, bankEntries: hit.bankEntries, added });
  }

  const data = { ...current, tours };
  if (bank !== undefined) data.stopBank = bank;
  return { data, report };
}
